import { IStackStyles, IStyle, mergeStyles, Stack, ZIndexes } from "@fluentui/react";
import React, { useMemo } from "react";
import { SIDEPANE_WIDTH } from "../styles";
import { usePcpLiveLessonContext } from "../store";
import { selectScreenOrientation } from "../store/selectors/pcpSharedSelectors";

interface CallWithSidepaneLayoutProps {
    mainContent: React.ReactNode;
    sidePaneContent: React.ReactNode;
    isMobile?: boolean;
    isSidePaneOpen?: boolean;
    styles?:  { root?: IStyle, main?: IStyle };
    //If 'overlay' then sideNave is shown in full-screen mode (when opened).
    sidepaneMode: 'overlay' | 'side';
}

const contentAndSidebarContainerStyles: IStackStyles = {
    root: {
        display: 'flex',
        flexFlow: 'row',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        position: 'relative',
    },
};

const contentAndSidebarContainerMobileStyles = (isLandscape: boolean): IStackStyles =>  ({
    root: {
        width: isLandscape ? '100%' : '100vw',
        height: '100%',
        position: 'relative',
        ...(isLandscape ? { paddingBottom: '0.5rem' } : {}),
    },
});


const mainContentDesktopStyles = (flexGrow: number, isSidePaneOpenAsOverlay?: boolean): IStackStyles => ({
    root: {
        width: '100%',
        flex: `${flexGrow}`,
        paddingTop: '0.5rem',
        paddingLeft: '0.5rem',
        paddingRight: '0.5rem',
        ...(isSidePaneOpenAsOverlay ? {display: 'none'} : {}) as object
    }
});

const mainContentMobileStyles: IStackStyles = {
    root: {
        width: '100%',
        flex: 5,
        paddingTop: '0.5rem',
    }
};

const sidePaneOpenDesktopStyles = (grow?: number): IStackStyles => ({
    root: {
        paddingTop: '0.5rem',
        paddingRight: '0.5rem',
        flex: grow ? `${grow} 1 0px` : `0 0 ${SIDEPANE_WIDTH}`,
    },
});
//SidePane is shown as overlay (the "#sidepane-container" element styles):
const sidePaneOpenMobileStyles = (): IStackStyles => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        width: '100vw',
        height: '100%',
        maxheight: '100%',

        position: 'fixed',
        zIndex: ZIndexes.Layer,
        padding: 0,
        gap: '0.5rem',
        transition: 'top 0.3s ease-out',    //make smooth second resize caused by setTimeout (while open or closing the on-screen keyboard in iOS)
    } as IStyle,
});


const sidePaneClosedStyles: IStackStyles = {
    root: {
        flex: 1,
        display: 'none',
    } as IStyle,
};

export const CallWithSidepaneLayout: React.FC<CallWithSidepaneLayoutProps> = ({
    isMobile,
    mainContent,
    sidePaneContent,
    isSidePaneOpen,
    styles,
    sidepaneMode
}): JSX.Element => {
    const orientation = usePcpLiveLessonContext(selectScreenOrientation);
    const isLandscape = orientation === 'landscape';

    const sidePaneOpenStyles = useMemo(
        () => sidepaneMode === 'overlay' ? sidePaneOpenMobileStyles() : sidePaneOpenDesktopStyles(isMobile && isLandscape ? 1 : undefined),
        [sidepaneMode, isMobile, isLandscape]
    );
    //TODO: add iOS check since "isSidePaneOpenAsOverlay" const is  used only for iPhone (also the const can be moved to separate react hook):
    const isSidePaneOpenAsOverlay = isSidePaneOpen && sidepaneMode === 'overlay';
    const sidepaneRef = React.useRef<HTMLDivElement>(null);

    const sidePaneStyleClasses = mergeStyles(
        (isSidePaneOpen ? sidePaneOpenStyles.root : sidePaneClosedStyles.root),
    );

    return (
        <div className={mergeStyles({ width: '100%', height: '100%'}, styles?.root || {})}>
            <Stack styles={isMobile ? contentAndSidebarContainerMobileStyles(isLandscape) : contentAndSidebarContainerStyles} horizontal={true}>
                <Stack.Item id="main-content-container" verticalFill
                    styles={
                        isMobile && !isLandscape ? {
                            root: {
                                ...mainContentMobileStyles.root as object,
                                ...(styles?.main || {}) as object,
                                ...(isSidePaneOpenAsOverlay ? {display: 'none'} : {}) as object
                            }
                        } : mainContentDesktopStyles(isLandscape ? 4 : 5, isSidePaneOpenAsOverlay)
                     }
                >
                    {mainContent}
                </Stack.Item>
                <div id='sidepane-container' className={sidePaneStyleClasses}
                    ref={sidepaneRef}
                >
                    {sidePaneContent}
                </div>
            </Stack>
        </div>
    );
};



